import { useRef, useState } from "react";
import { Camera, Loader2, Trash2, Upload } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@bookdental/ui";
import { toast } from "sonner";
import { MAX_AVATAR_BINARY_BYTES, MAX_AVATAR_DATA_URL_LENGTH } from "@bookdental/schemas";
import { CameraCapture } from "./CameraCapture";
import { getPatientColor, patientGradient } from "@/lib/patient-color";
import { AJUDA_PACIENTES } from "@/lib/ajuda/dialogos/pacientes";

type Props = {
  open: boolean;
  onClose: () => void;
  current: string | null;
  patientName: string;
  onSave: (dataUrl: string | null) => Promise<void> | void;
};

export function AvatarEditor({ open, onClose, current, patientName, onSave }: Props) {
  const [mode, setMode] = useState<"menu" | "camera">("menu");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const color = getPatientColor(patientName);
  const initial = patientName.charAt(0).toUpperCase() || "?";

  const handleClose = () => {
    onClose();
    setTimeout(() => setMode("menu"), 200);
  };

  const save = async (dataUrl: string | null) => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(dataUrl);
      handleClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar foto");
    } finally {
      setSaving(false);
    }
  };

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem");
      return;
    }
    if (file.size > MAX_AVATAR_BINARY_BYTES) {
      toast.error("Imagem muito grande (máximo 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (dataUrl.length > MAX_AVATAR_DATA_URL_LENGTH) {
        toast.error("Imagem muito grande após codificação");
        return;
      }
      save(dataUrl);
    };
    reader.onerror = () => toast.error("Falha ao ler arquivo");
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle ajuda={mode === "camera" ? AJUDA_PACIENTES.tirarFoto : AJUDA_PACIENTES.fotoDoPaciente}>
            {mode === "camera" ? "Tirar foto" : "Foto do paciente"}
          </DialogTitle>
        </DialogHeader>

        {mode === "menu" && (
          <div className="space-y-4">
            <div className="flex justify-center">
              {current ? (
                <img
                  src={current}
                  alt=""
                  className="h-32 w-32 rounded-full object-cover ring-2 ring-white shadow-md"
                />
              ) : (
                <div
                  className="flex h-32 w-32 items-center justify-center rounded-full text-4xl font-bold text-white ring-2 ring-white shadow-md"
                  style={{ background: patientGradient(color) }}
                >
                  {initial}
                </div>
              )}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = "";
              }}
            />

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={saving}
              >
                <Upload className="h-4 w-4" />
                Enviar arquivo
              </Button>
              <Button
                variant="outline"
                onClick={() => setMode("camera")}
                disabled={saving}
              >
                <Camera className="h-4 w-4" />
                Usar câmera
              </Button>
            </div>

            {current && (
              <Button
                variant="outline"
                onClick={() => save(null)}
                disabled={saving}
                className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Remover foto
              </Button>
            )}

            <p className="text-center text-[11px] text-zinc-400">
              JPG ou PNG até 2MB
            </p>
          </div>
        )}

        {mode === "camera" && (
          <CameraCapture
            onCapture={save}
            onCancel={() => setMode("menu")}
            saving={saving}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
